// Which work-authorisation status a candidate's work-auth type gives them, and which work documents
// complete their Integrity Check. The server judges completion by these rules and every screen
// lists documents from them, so both always agree.
//
// A type or EAD category may be stored as its option key or its option label (the profile forms
// store labels), so every function here accepts either. The keys and labels below mirror
// workAuthTypeOptions and eadCategoryOptions in src/config/fieldOptions.js; a test checks that they
// stay the same.
//
// Switches are passed in as arguments; nothing here reads the environment. Written as plain
// CommonJS, importing only files written the same way, so the API server, which runs its files
// under plain node, can require this same file the browser bundle imports.

const { normalizeScope, INTEGRITY_CHECK_SCOPES } = require('./integrityCheckScope');

const WORK_AUTH_TIERS = Object.freeze({
  A: 'Authorized to work in the U.S. and requires current/future employer sponsorship',
  B: 'Authorized to work in the U.S. for a limited duration (with valid work authorization/EAD)',
  C: 'Authorized to work in the U.S. without employer sponsorship',
});

const WORK_DOCUMENTS = Object.freeze({
  i94: 'Form I-94',
  i20: 'Form I-20',
  ead: 'Employment Authorization Document (Form I-766)',
  us_passport: 'U.S. Passport',
  us_passport_card: 'U.S. Passport Card',
  permanent_resident_card: 'Permanent Resident Card (Form I-551)',
  social_security_card: 'Social Security Card (unrestricted)',
  us_birth_certificate: 'U.S. Birth Certificate (certified copy)',
  birth_abroad_certificate: 'Certification of Birth Abroad (Forms DS-1350, FS-545, FS-240)',
  naturalization_certificate: 'Certificate of Naturalization (Form N-550 or N-570)',
  citizenship_certificate: 'Certificate of Citizenship (Form N-560 or N-561)',
  ds2019: 'Form DS-2019',
});

// Names the two citizenship certificates were recorded under before they took the names above. A
// verification record under either name is the same document; only the names above are shown.
const FORMER_WORK_DOCUMENT_NAMES = Object.freeze({
  'Certificate of Naturalization (Form N-550/N-570)': 'naturalization_certificate',
  'Certificate of Citizenship (Form N-560/N-561)': 'citizenship_certificate',
});

const comparableName = name => (typeof name === 'string' ? name.trim().toLowerCase() : '');

const WORK_DOCUMENT_CODES_BY_NAME = new Map(
  Object.keys(WORK_DOCUMENTS)
    .map(code => [comparableName(WORK_DOCUMENTS[code]), code])
    .concat(
      Object.keys(FORMER_WORK_DOCUMENT_NAMES).map(name => [comparableName(name), FORMER_WORK_DOCUMENT_NAMES[name]])
    )
);

/**
 * The work document a name (as shown, or as a verification record carries it) stands for.
 *
 * @param {*} name
 * @returns {string | null} its code in WORK_DOCUMENTS, or null for any other name
 */
function workDocumentCodeOfName(name) {
  return WORK_DOCUMENT_CODES_BY_NAME.get(comparableName(name)) || null;
}

/**
 * The name to show for a document: a work document's own name, whichever name it was recorded
 * under; any other name is given back unchanged.
 */
function workDocumentNameOf(name) {
  const code = workDocumentCodeOfName(name);
  return code ? WORK_DOCUMENTS[code] : name;
}

/**
 * Whether two names stand for the same document, ignoring case and surrounding spaces and
 * matching a work document under any name it was recorded under.
 */
function sameDocumentName(a, b) {
  const codeA = workDocumentCodeOfName(a);
  if (codeA) return codeA === workDocumentCodeOfName(b);
  return comparableName(a) !== '' && comparableName(a) === comparableName(b);
}

const US_CITIZEN_SETS = [['us_passport'], ['us_passport_card'], ['us_birth_certificate'], ['birth_abroad_certificate']];
// Offered and counted only while the additional work documents are switched on.
const US_CITIZEN_ADDITIONAL_SETS = [['naturalization_certificate'], ['citizenship_certificate']];

const I94_ONLY = { tier: 'A', sets: () => [['i94']] };
const i94Only = label => Object.assign({ label }, I94_ONLY);
// With the additional documents on, an exchange visitor also shows their DS-2019.
const exchangeVisitorSets = additional => (additional ? [['i94', 'ds2019']] : [['i94']]);
const EAD_ONLY = () => [['ead']];
const SSN_OR_EAD = () => [['social_security_card'], ['ead']];
const PERMANENT_EAD_CATEGORIES = ['a3', 'a5', 'a10'];

// Each type: its option label, its tier (EAD's follows its category), its accepted sets (one of
// them is needed; every document in a set is needed) and the EAD categories it accepts.
const WORK_AUTH_TYPES = {
  'H-1B': i94Only('H-1B'),
  'H-1B1': i94Only('H-1B1'),
  'E-3': i94Only('E-3'),
  'O-1': i94Only('O-1'),
  'L-1A': i94Only('L-1A'),
  'L-1B': i94Only('L-1B'),
  TN: i94Only('TN'),
  'R-1': i94Only('R-1'),
  'P-1/P-2/P-3': i94Only('P-1 / P-2 / P-3'),
  'Q-1': i94Only('Q-1'),
  'E-1': i94Only('E-1'),
  'E-2': i94Only('E-2'),
  'J-1': { label: 'J-1', tier: 'A', sets: exchangeVisitorSets },
  L2S: { label: 'L2S', tier: 'B', sets: () => [['i94']] },
  'F-1 OPT': { label: 'F-1 OPT', tier: 'B', sets: EAD_ONLY, eadCategories: ['c3a', 'c3b'] },
  'STEM OPT': { label: 'F-1 STEM OPT Extension', tier: 'B', sets: EAD_ONLY, eadCategories: ['c3c'] },
  CPT: { label: 'CPT', tier: 'B', sets: () => [['i20', 'i94']] },
  'J-1 Academic Training': { label: 'J-1 Academic Training', tier: 'B', sets: exchangeVisitorSets },
  'M-1 Practical Training': { label: 'M-1 Practical Training', tier: 'B', sets: EAD_ONLY, eadCategories: ['c6'] },
  EAD: { label: 'Employment Authorization Document (EAD)', tier: null, sets: EAD_ONLY, eadCategories: 'any' },
  'US Citizen': {
    label: 'U.S. Citizen',
    tier: 'C',
    sets: additional => (additional ? US_CITIZEN_SETS.concat(US_CITIZEN_ADDITIONAL_SETS) : US_CITIZEN_SETS),
  },
  'Green Card': { label: 'Lawful Permanent Residents', tier: 'C', sets: () => [['permanent_resident_card']] },
  Asylee: { label: 'Asylees', tier: 'C', sets: SSN_OR_EAD, eadCategories: PERMANENT_EAD_CATEGORIES },
  Refugee: { label: 'Refugees', tier: 'C', sets: SSN_OR_EAD, eadCategories: PERMANENT_EAD_CATEGORIES },
  'Withholding of Removal': {
    label: 'Individuals granted withholding of removal',
    tier: 'C',
    sets: SSN_OR_EAD,
    eadCategories: PERMANENT_EAD_CATEGORIES,
  },
  'Cuban/Haitian Entrant': {
    label: 'Cuban/Haitian entrants with permanent authorization',
    tier: 'C',
    sets: SSN_OR_EAD,
    eadCategories: PERMANENT_EAD_CATEGORIES,
  },
  'Permanent Work Auth': {
    label: 'Individuals with permanent work authorization not tied to an employer',
    tier: 'C',
    sets: SSN_OR_EAD,
    eadCategories: PERMANENT_EAD_CATEGORIES,
  },
};

// The EAD categories a candidate may hold, key → label. (c)(15) is not in use and is not one.
const EAD_CATEGORIES = {
  a2: '(a)(2) - Lawful temporary resident',
  a3: '(a)(3) - Refugee',
  a4: '(a)(4) - Paroled refugee',
  a5: '(a)(5) - Asylee',
  a6: '(a)(6) - Fiancé(e) (K-1 or K-2 nonimmigrant)',
  a7: '(a)(7) - N-8 or N-9',
  a8: '(a)(8) - Citizen of Micronesia, Marshall Islands, or Palau',
  a9: '(a)(9) - K-3 or K-4',
  a10: '(a)(10) - Withholding of deportation or removal granted',
  a11: '(a)(11) - Deferred Enforced Departure',
  a12: '(a)(12) - Temporary Protected Status granted',
  a13: '(a)(13) - Family Unity Program (Section 301 of the Immigration Act of 1990)',
  a14: '(a)(14) - LIFE Legalization (Section 1504 of the Legal Immigrant Family Equity (LIFE) Act Amendments)',
  a15: '(a)(15) - V visa nonimmigrant',
  a16: '(a)(16) - T-1 nonimmigrant',
  a17: '(a)(17) - Spouse of an E nonimmigrant',
  a18: '(a)(18) - Spouse of an L nonimmigrant',
  a19: '(a)(19) - U-1 nonimmigrant',
  a20: '(a)(20) - U-2, U-3, U-4, or U-5 nonimmigrant',
  c1: '(c)(1) - Spouse/dependent of A-1 or A-2 visa nonimmigrant',
  c2: '(c)(2) - Spouse/dependent of Coordination Council for North American Affairs (E-1)/ Taipei Economic and Cultural Representative Office (TECRO)',
  c3a: '(c)(3)(A) - F-1 student, pre-completion Optional Practical Training',
  c3b: '(c)(3)(B) - F-1 student, post-completion Optional Practical Training',
  c3c: '(c)(3)(C) - F-1 student, 24-month extension for STEM students',
  c3ii: '(c)(3)(ii) - F-1 student, off-campus employment sponsored by a qualifying international organization',
  c3iii: '(c)(3)(iii) - F-1 student, off-campus employment due to severe economic hardship',
  c4: '(c)(4) - Spouse/dependent of G-1, G-3, or G-4',
  c5: '(c)(5) - J-2 spouse or child of J-1 exchange visitor',
  c6: '(c)(6) - M-1 student, Practical Training',
  c7: '(c)(7) - Dependent of NATO-1 through NATO-6',
  c8_post_jan4_1995: '(c)(8) - Asylum application pending filed on/after Jan. 4, 1995',
  c8_pre_jan4_1995_not_in_proceedings:
    '(c)(8) - Asylum application pending filed before Jan. 4, 1995 and applicant is not in exclusion/deportation proceedings',
  c8_pre_jan4_1995_in_proceedings:
    '(c)(8) - Asylum application pending filed before Jan. 4, 1995 and applicant is in exclusion/deportation proceedings',
  c8_abc: '(c)(8) - Asylum application under ABC Agreement',
  c9: '(c)(9) - Pending adjustment of status under Section 245 of the Act',
  c10: '(c)(10) - Suspension of deportation applicants (filed before April 1, 1997) Cancellation of Removal applicants Cancellation applicants under NACARA',
  c11: '(c)(11) - Public Interest parolee',
  c12: '(c)(12) - Spouse of an E-2 CNMI investor',
  c14: '(c)(14) - Deferred action',
  c16: '(c)(16) - Creation of record (adjustment based on continuous residence since Jan. 1, 1972)',
  c17i: '(c)(17)(i) - B-1 domestic servant of certain nonimmigrants',
  c17ii: '(c)(17)(ii) - B-1 domestic servant of certain U.S. citizens who are in the United States on a temporary basis.',
  c17iii: '(c)(17)(iii) - Certain B-1 nonimmigrant employees of a foreign airline',
  c18: '(c)(18) - Order of supervision',
  c19: '(c)(19) - Certain pending TPS applicants whom USCIS has determined are prima facie eligible for TPS and who may then receive an EAD as a "temporary treatment benefit" under 8 C.F.R. 244.10(a).',
  c20: '(c)(20) - Section 210 legalization (pending I-700)',
  c21: '(c)(21) - S visa nonimmigrant',
  c22: '(c)(22) - Section 245A legalization (pending I-687)',
  c23: '(c)(23) - Irish peace process (Q-2)',
  c24: '(c)(24) - LIFE legalization',
  c25: '(c)(25) - T-2, T-3, T-4, T-5, or T-6 nonimmigrant',
  c26: '(c)(26) - Spouse of an H-1B nonimmigrant',
  c31: '(c)(31) - VAWA self-petitioners with an approved Form I-360',
  c33: '(c)(33) - Consideration of Deferred Action for Childhood Arrivals (DACA)',
  c35: '(c)(35) - Principal beneficiary of an approved employment-based immigrant petition facing compelling circumstances',
  c36: '(c)(36) - Spouse or unmarried child of a principal beneficiary of an approved employment-based immigrant petition facing compelling circumstances',
};

const lookupKey = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

// key or label (any case, surrounding space ignored) → key
const indexByKeyAndLabel = (table, labelOf) => {
  const index = new Map();
  Object.keys(table).forEach(key => {
    index.set(lookupKey(key), key);
    index.set(lookupKey(labelOf(table[key])), key);
  });
  return index;
};

const TYPE_INDEX = indexByKeyAndLabel(WORK_AUTH_TYPES, type => type.label);
const EAD_CATEGORY_INDEX = indexByKeyAndLabel(EAD_CATEGORIES, label => label);

/**
 * @param {*} labelOrKey - a work-auth type as stored on a profile
 * @returns {string | null} the type's option key, or null when it is not a known type
 */
function workAuthTypeKeyOf(labelOrKey) {
  const key = lookupKey(labelOrKey);
  return (key && TYPE_INDEX.get(key)) || null;
}

/**
 * @param {*} labelOrKey - an EAD category as stored on a profile
 * @returns {string | null} the category's option key, or null when it is not a category a
 *   candidate may hold (including (c)(15), which is no longer in use)
 */
function eadCategoryKeyOf(labelOrKey) {
  const key = lookupKey(labelOrKey);
  return (key && EAD_CATEGORY_INDEX.get(key)) || null;
}

// A card code is the letter, a two-digit number and any sub-part, upper-cased: C09, A05, C03B.
const CARD_CODE_PATTERN = /^([AC])0*(\d{1,2})([A-Z]*)$/;

/**
 * The category as the card prints it, so a category picked on the profile and one read from the
 * card can be compared. All four (c)(8) options are the one card code C08.
 *
 * @param {*} keyOrLabelOrCardText - an option key, an option label, or text read from the card
 *   (`C09`, `c 9`, `(c)(9)`)
 * @returns {string | null}
 */
function eadCardCodeOf(keyOrLabelOrCardText) {
  if (typeof keyOrLabelOrCardText !== 'string') return null;
  const optionKey = eadCategoryKeyOf(keyOrLabelOrCardText);
  // A label carries a description after " - "; only the part before it is the category.
  const source = optionKey ? optionKey.replace(/_.*$/, '') : keyOrLabelOrCardText.split(' - ')[0];
  const match = source.replace(/[^A-Za-z0-9]/g, '').toUpperCase().match(CARD_CODE_PATTERN);
  if (!match) return null;
  const letter = match[1];
  const number = match[2];
  const subPart = match[3];
  return `${letter}${number.padStart(2, '0')}${subPart}`;
}

/**
 * @param {{ workAuthType?: *, eadCategory?: * }} profile
 * @returns {string | null} one of WORK_AUTH_TIERS, or null with no type, an unknown type, or type
 *   EAD without a category
 */
function workAuthTierFor({ workAuthType, eadCategory } = {}) {
  const typeKey = workAuthTypeKeyOf(workAuthType);
  if (!typeKey) return null;
  if (typeKey !== 'EAD') return WORK_AUTH_TIERS[WORK_AUTH_TYPES[typeKey].tier];

  const categoryKey = eadCategoryKeyOf(eadCategory);
  if (!categoryKey) return null;
  return PERMANENT_EAD_CATEGORIES.includes(categoryKey) ? WORK_AUTH_TIERS.C : WORK_AUTH_TIERS.B;
}

/**
 * @param {*} workAuthType - key or label
 * @returns {string[] | 'any' | null} the EAD category keys the type accepts, 'any' for type EAD,
 *   or null for a type that accepts no EAD
 */
function eadCategoriesAcceptedBy(workAuthType) {
  const typeKey = workAuthTypeKeyOf(workAuthType);
  const accepted = typeKey ? WORK_AUTH_TYPES[typeKey].eadCategories : undefined;
  if (!accepted) return null;
  return accepted === 'any' ? 'any' : accepted.slice();
}

const US_CITIZENSHIP_PATTERN = /^(United States|US|USA|United States of America)$/i;

/**
 * Whether the candidate must also hold a US citizenship document, whatever their type: their
 * country of citizenship is the United States, or they said they are eligible for roles requiring
 * US citizenship.
 *
 * @param {{ countryOfCitizenship?: *, eligibleForRolesInUs?: * }} profile
 * @returns {boolean}
 */
function citizenshipDocumentRequired({ countryOfCitizenship, eligibleForRolesInUs } = {}) {
  const usCitizen = typeof countryOfCitizenship === 'string' && US_CITIZENSHIP_PATTERN.test(countryOfCitizenship.trim());
  const eligible = eligibleForRolesInUs === true || lookupKey(eligibleForRolesInUs) === 'yes';
  return usCitizen || eligible;
}

/**
 * Whether the candidate is a US citizen, who needs no foreign passport: their country of
 * citizenship names the United States (whatever its case or surrounding spaces), or their
 * work-auth type is US Citizen, whatever country an older profile still shows. Every path that
 * decides whether a foreign passport is needed asks this.
 *
 * @param {{ countryOfCitizenship?: *, workAuthType?: * }} profile
 * @returns {boolean}
 */
function isUsCitizenProfile(profile) {
  const { countryOfCitizenship, workAuthType } = profile && typeof profile === 'object' ? profile : {};
  const byCountry = typeof countryOfCitizenship === 'string' && US_CITIZENSHIP_PATTERN.test(countryOfCitizenship.trim());
  return byCountry || workAuthTypeKeyOf(workAuthType) === 'US Citizen';
}

const copySets = sets => sets.map(set => set.slice());

/**
 * The work-document sets that complete a candidate's check. One set of `typeSets` is needed, and
 * where citizenship requires it, one set of `citizenshipSets` as well; a set meeting both counts
 * for both. An EAD in a set counts only in a category the type accepts (see contentRequirementFor).
 *
 * @param {{ workAuthType?: *, eadCategory?: *, citizenshipRequired?: boolean,
 *   additionalDocuments?: boolean }} args - `additionalDocuments` is whether the naturalization and
 *   citizenship certificates and DS-2019 are switched on
 * @returns {{ typeSets: string[][], citizenshipSets: string[][] | null } | null} null with no type
 *   or an unknown type
 */
function acceptedWorkDocumentSets({ workAuthType, citizenshipRequired, additionalDocuments } = {}) {
  const typeKey = workAuthTypeKeyOf(workAuthType);
  if (!typeKey) return null;
  const additional = additionalDocuments === true;
  return {
    typeSets: copySets(WORK_AUTH_TYPES[typeKey].sets(additional)),
    citizenshipSets: citizenshipRequired === true ? copySets(WORK_AUTH_TYPES['US Citizen'].sets(additional)) : null,
  };
}

/**
 * What the provider must have read from a document before it counts for this type.
 *
 * @param {*} workAuthType - key or label
 * @param {string} code - a WORK_DOCUMENTS code
 * @returns {'i94_class_l2s' | 'i20_cpt' | 'ead_category' | null}
 */
function contentRequirementFor(workAuthType, code) {
  const typeKey = workAuthTypeKeyOf(workAuthType);
  if (!typeKey) return null;
  if (typeKey === 'L2S' && code === 'i94') return 'i94_class_l2s';
  if (typeKey === 'CPT' && code === 'i20') return 'i20_cpt';
  if (code === 'ead' && eadCategoriesAcceptedBy(typeKey) !== null) return 'ead_category';
  return null;
}

/**
 * Whether an I-94's class of admission reads L-2S, ignoring case, spaces and punctuation.
 *
 * @param {*} value
 * @returns {boolean}
 */
function i94ClassIsL2S(value) {
  return typeof value === 'string' && value.replace(/[^A-Za-z0-9]/g, '').toUpperCase() === 'L2S';
}

const YES_VALUES = new Set(['true', 'yes', 'y']);
const NO_VALUES = new Set(['false', 'no', 'n']);

/**
 * Whether an I-20 was read as authorising CPT.
 *
 * @param {*} value
 * @returns {boolean | null} null when the value says neither yes nor no
 */
function cptAuthorised(value) {
  if (typeof value === 'boolean') return value;
  const text = lookupKey(value);
  if (YES_VALUES.has(text)) return true;
  if (NO_VALUES.has(text)) return false;
  return null;
}

/**
 * The display names of every document in the accepted sets, in order and once each.
 *
 * @param {Object} args - as for acceptedWorkDocumentSets
 * @returns {string[]}
 */
function acceptedWorkDocumentNames(args) {
  const answer = acceptedWorkDocumentSets(args);
  if (!answer) return [];
  const codes = answer.typeSets.concat(answer.citizenshipSets || []).flat();
  return Array.from(new Set(codes)).map(code => WORK_DOCUMENTS[code]);
}

const isSubsetOf = (small, large) => small.every(code => large.includes(code));

// Every way to meet one set of `first` and one set of `second` at once, each as one set, leaving
// out any that asks for more than another already does.
function combinedSets(first, second) {
  const combined = first.flatMap(a => second.map(b => Array.from(new Set(a.concat(b)))));
  return combined.filter(
    (set, index) =>
      !combined.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          isSubsetOf(other, set) &&
          (other.length < set.length || otherIndex < index)
      )
  );
}

/**
 * The documents an Integrity Check of this scope asks of the candidate, decided from their
 * profile as the server read it from their user record. Every check asks for a licence or State
 * ID, and a foreign passport unless they are a US citizen. Only an `identity_work` check asks for
 * work documents: any one of `sets`, every document in that set, by name.
 *
 * @param {Object} [args]
 * @param {{ workAuthType?: *, eadCategory?: *, countryOfCitizenship?: *, eligibleForRolesInUs?: * }}
 *   [args.profile]
 * @param {*} [args.scope] - a scope as stored; a missing one reads `identity_work`
 * @param {boolean} [args.additionalDocuments] - whether the naturalization and citizenship
 *   certificates and DS-2019 are switched on
 * @returns {{ licence: true, foreignPassport: boolean,
 *   work: null | { sets: string[][], workAuthTypeMissing: boolean } }}
 */
function integrityRequirementsFor({ profile, scope, additionalDocuments } = {}) {
  const candidate = profile && typeof profile === 'object' ? profile : {};
  const identity = { licence: true, foreignPassport: !isUsCitizenProfile(candidate) };
  if (normalizeScope(scope) === INTEGRITY_CHECK_SCOPES.IDENTITY) return Object.assign({}, identity, { work: null });

  const accepted = acceptedWorkDocumentSets({
    workAuthType: candidate.workAuthType,
    citizenshipRequired: citizenshipDocumentRequired(candidate),
    additionalDocuments: additionalDocuments === true,
  });
  if (!accepted) return Object.assign({}, identity, { work: { sets: [], workAuthTypeMissing: true } });

  const sets = accepted.citizenshipSets ? combinedSets(accepted.typeSets, accepted.citizenshipSets) : accepted.typeSets;
  return Object.assign({}, identity, {
    work: { sets: sets.map(set => set.map(code => WORK_DOCUMENTS[code])), workAuthTypeMissing: false },
  });
}

module.exports = {
  WORK_AUTH_TIERS,
  WORK_DOCUMENTS,
  workDocumentCodeOfName,
  workDocumentNameOf,
  sameDocumentName,
  workAuthTypeKeyOf,
  eadCategoryKeyOf,
  eadCardCodeOf,
  workAuthTierFor,
  eadCategoriesAcceptedBy,
  acceptedWorkDocumentSets,
  citizenshipDocumentRequired,
  contentRequirementFor,
  i94ClassIsL2S,
  cptAuthorised,
  acceptedWorkDocumentNames,
  isUsCitizenProfile,
  integrityRequirementsFor,
};
